import { ReactElement, ReactNode } from 'react';
import { GestureResponderEvent } from 'react-native';
import { Card } from '@/common/components/Card/Card';
import { Heading } from '@/common/components/Heading/Heading';
import { IconBadge } from '@/common/components/IconBadge/IconBadge';
import { TIconProps } from '@/common/components/Icons/Icon';
import { Label } from '@/common/components/Label/Label';
import { SkeletonInline } from '@/common/components/SkeletonInline/SkeletonInline';
import { Stack } from '@/common/components/Stack/Stack';
import { TColors } from '@coverflex-tech/hypernova-design-tokens';
import { styles } from './CardPocket.styles';

export type TCardPocketProps = {
  title: string;
  icon?: ReactElement<TIconProps>;
  iconBadgeColor?: keyof TColors['light']['semantic']['surface'];
  backgroundColor?: keyof TColors['light']['semantic']['surface'];
  values: TCardPocketValueProps[];
  onPress?: (event?: GestureResponderEvent) => void;
  children?: ReactNode;
};

export function CardPocket({
  title,
  icon,
  iconBadgeColor = 'accentSecondary20',
  backgroundColor,
  values,
  children,
  onPress,
}: TCardPocketProps) {
  return (
    <Card onPress={onPress} className={styles.root({ backgroundColor })}>
      <Stack gap="sm">
        <Stack horizontal gap="md" justify="between" align="center">
          <Heading level={4}>{title}</Heading>
          {icon && (
            <IconBadge
              ratio={32}
              icon={icon}
              backgroundColor={iconBadgeColor}
            />
          )}
        </Stack>
        <Stack horizontal wrap gap="md" align="end" className={styles.values}>
          {values.map((value, index) => (
            <CardPocketValue key={index} {...value} />
          ))}
        </Stack>
        {children}
      </Stack>
    </Card>
  );
}

export type TCardPocketValueProps = {
  label?: ReactNode;
  value?: ReactNode;
  type?: 'primary' | 'secondary';
  loading?: boolean;
};

function CardPocketValue({
  label,
  value,
  type = 'primary',
  loading = false,
}: TCardPocketValueProps) {
  if (loading) {
    return (
      <Stack gap="2xs">
        <SkeletonInline className={styles.loading({ type })} />
        <SkeletonInline className={styles.loadingLabel} />
      </Stack>
    );
  }

  return (
    <Stack shrink>
      <Label
        size={type === 'primary' ? 'xl' : 'lg'}
        bold
        className={type === 'secondary' ? 'mb-[0.2rem]' : ''}
      >
        {value ?? ' '}
      </Label>
      {label && <Label size="sm">{label}</Label>}
    </Stack>
  );
}

export function CardPocketSkeleton() {
  return (
    <Card className={styles.root()}>
      <Stack gap="sm">
        <Stack horizontal gap="md" justify="between" align="center">
          <SkeletonInline className={styles.loading({ type: 'primary' })} />
          <SkeletonInline className={styles.loadingIcon} />
        </Stack>
        <Stack horizontal wrap gap="md" align="end" className={styles.values}>
          <SkeletonInline className={styles.loading({ type: 'primary' })} />
        </Stack>
      </Stack>
    </Card>
  );
}
